import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { InvoiceStatus, useSessionInvoices, useSessionOrders } from '@app/api';
import { toEuros } from '@app/i18n';

import {
  computeAlreadyBilledAmount,
  computeToBeBilledAmount,
} from 'lib/orders';
import { SessionBoxInfo } from 'components';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({}));

interface Props {
  onClick?: () => any;
  className?: string;
  sessionId: string;
}

export const BillingBox = ({ onClick, className, sessionId }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: orders } = useSessionOrders(sessionId);
  const { data: invoices } = useSessionInvoices(sessionId);

  const toBeBilled = computeToBeBilledAmount(orders);
  const alreadyBilled = computeAlreadyBilledAmount(orders);

  const totalToPay = useMemo(() => {
    if (!invoices) return 0;
    return invoices.reduce((acc, invoice) => {
      if (invoice.status !== InvoiceStatus.OPEN || !invoice.total) {
        return acc;
      }
      return acc + invoice.total;
    }, 0);
  }, [invoices]);

  const text = useMemo(() => {
    if (totalToPay) {
      return t('sessions.details.boxes.billing.toPay', {
        amount: totalToPay,
      });
    }
    if (alreadyBilled && !toBeBilled) {
      return t('sessions.details.boxes.billing.alreadyBilled', {
        amount: alreadyBilled,
      });
    }

    return t('sessions.details.boxes.billing.toBeBilled', {
      amount: toBeBilled,
    });
  }, [t, toBeBilled, alreadyBilled, totalToPay]);

  return (
    <SessionBoxInfo
      color="green"
      title={t('sessions.details.boxes.billingTitle')}
      className={className}
      icon="ri-file-list-3-line"
      onClick={onClick}
    >
      {text}
    </SessionBoxInfo>
  );
};
